import { Button } from '@components/Buttons/Base'
import { findIconDefinition, FontAwesomeIcon } from '@purposity/icons'
import * as Colors from '@themes/Colors'
import Fonts from '@themes/Fonts'
import { QRCodeCanvas } from 'qrcode.react'
import React, { useCallback, useEffect, useRef, useState } from 'react'
import Modal from 'react-modal'
import { Pressable, StyleSheet, Text, View } from 'react-native'

export function QrModal({
  value,
  isOpen,
  setIsOpen,
}: {
  value: string
  isOpen: boolean
  setIsOpen: (isOpen: boolean) => void
}) {
  const qrCodeRef = useRef<HTMLCanvasElement>(null)
  const [logoDataUrl, setLogoDataUrl] = useState<string | null>(null)

  // Convert the logo image to a data URL to avoid tainted canvas issues
  useEffect(() => {
    const img = new Image()
    img.crossOrigin = 'anonymous'
    img.onload = () => {
      const canvas = document.createElement('canvas')
      canvas.width = img.width
      canvas.height = img.height
      const ctx = canvas.getContext('2d')
      if (ctx) {
        ctx.drawImage(img, 0, 0)
        setLogoDataUrl(canvas.toDataURL('image/png'))
      }
    }
    img.src = 'https://www.purposity.com/mark_pink.svg'
  }, [])

  const handleCopyShareLink = useCallback(() => {
    setTimeout(() => {
      navigator.clipboard.writeText(value)
    }, 500)
  }, [])

  const handleDownloadQR = useCallback(() => {
    if (qrCodeRef.current) {
      try {
        const downloadLink = document.createElement('a')
        downloadLink.href = qrCodeRef.current.toDataURL('image/png')
        downloadLink.download = 'qr-code.png'
        document.body.appendChild(downloadLink)
        downloadLink.click()
        document.body.removeChild(downloadLink)
      } catch (error) {
        console.error('Error downloading QR code:', error)
        // Fallback: Open in new tab
        const downloadLink = document.createElement('a')
        downloadLink.href = qrCodeRef.current.toDataURL('image/png')
        downloadLink.target = '_blank'
        downloadLink.rel = 'noopener noreferrer'
        document.body.appendChild(downloadLink)
        downloadLink.click()
        document.body.removeChild(downloadLink)
      }
    }
  }, [])

  return (
    <Modal
      isOpen={isOpen}
      preventScroll
      shouldCloseOnOverlayClick={true}
      onRequestClose={() => {
        setIsOpen(false)
      }}
      style={{
        overlay: {
          backgroundColor: Colors.darkBlue85,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
        },
        content: {
          inset: 'unset',
          display: 'flex',
          flexGrow: 1,
          flexShrink: 0,
          flexBasis: '100%',
          border: 0,
          padding: 0,
          backgroundColor: 'unset',
        },
      }}
    >
      <View style={styles.container}>
        <View style={styles.innerModal}>
          <Button
            format="rounded"
            emphasis="tertiary"
            onClick={() => {
              setIsOpen(false)
            }}
            title="×"
            style={{
              position: 'absolute',
              top: 16,
              right: 16,
              zIndex: 1,
              fontSize: 38,
              backgroundColor: 'transparent',
              border: 'none',
              color: Colors.textGray,
              width: 'auto',
              minWidth: 'unset',
              height: 'auto',
              padding: 0,
            }}
            padding={{
              paddingTop: 0,
              paddingRight: 0,
              paddingBottom: 0,
              paddingLeft: 0,
            }}
          />
          <View style={authStyles.textContainer}>
            <Text style={authStyles.textHeader}>Share with your friends</Text>
            <Text style={authStyles.textDescriptiopn}>
              Scan the QR code to share with your friends.
            </Text>
            <View style={{ margin: 10 }}>
              <QRCodeCanvas
                ref={qrCodeRef}
                value={value}
                size={256}
                imageSettings={
                  logoDataUrl
                    ? {
                      src: logoDataUrl,
                      height: 50,
                      width: 50,
                      excavate: true,
                    }
                    : undefined
                }
              />
            </View>

            <View style={authStyles.textDescriptiopn}>
              <Pressable
                onPress={handleDownloadQR}
                style={styles.downloadButton}
              >
                <FontAwesomeIcon
                  icon={findIconDefinition({
                    prefix: 'fas',
                    iconName: 'download',
                  })}
                  color={Colors.black}
                  size={16}
                />
                <Text style={styles.downloadText}>Download QR Code</Text>
              </Pressable>
            </View>
          </View>
        </View>
      </View>
    </Modal>
  )
}

const styles = StyleSheet.create({
  container: {
    flexGrow: 1,
    flexShrink: 0,
    flexBasis: '100%',

    justifyContent: 'space-around',
    alignItems: 'center',
  },
  innerModal: {
    width: '100%',
    maxWidth: 500,
    minHeight: 500,
    maxHeight: Math.pow(2, 9),
    paddingVertical: Math.pow(2, 5),
    paddingHorizontal: 48,
    backgroundColor: Colors.backgroundGray,

    borderRadius: Math.pow(2, 4),
    gap: Math.pow(2, 5),
  },
  downloadButton: {
    flexDirection: 'row',
    alignItems: 'center',
    backgroundColor: Colors.white,
    paddingVertical: 8,
    paddingHorizontal: 16,
    borderRadius: 6,
    borderWidth: 1,
    borderColor: Colors.black,
    marginTop: 16,
  },
  downloadText: {
    marginLeft: 8,
    fontSize: 14,
    fontWeight: '600',
    color: Colors.black,
    fontFamily: Fonts.type.base,
  },
})

const authStyles = StyleSheet.create({
  textContainer: {
    flex: 16,
    alignItems: 'center',
    justifyContent: 'center',
    gap: Math.pow(2, 2),
  },
  actionContainer: {
    display: 'grid',
    flexShrink: 0,

    columnGap: Math.pow(2, 3),
    rowGap: Math.pow(2, 4),
    gridTemplateColumns: 'repeat(auto-fit, minmax(198px, 1fr))',
  },

  textHeader: {
    fontFamily: Fonts.type.bold,
    fontSize: Fonts.size.h4,
    color: Colors.coal,
    textAlign: 'center',
    margin: 0,
  },

  textDescriptiopn: {
    textAlign: 'center',
    color: Colors.textGray,
    fontFamily: Fonts.type.base,
    fontSize: Fonts.size.regular,
    margin: 0,
  },
})
